import { useEffect, useState } from 'react'
import Paper from '@/components/Paper'
import SubHeaders from '@/components/Headers/SubHeaders'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { FormItem, Typogaphy } from 'vnetwork-ui'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import DomainsFilterCard from '../shared/DomainsFilterCard'
import ReportServiceSelect from '../shared/ReportServiceSelect'
import ServiceReportMailerHint from '../shared/ServiceReportMailerHint'
import { UpdateReportPayload } from '../../types/payload'
import { getReportWaf, updateReportWaf } from '../../services'
import {
    sendFormatOptions,
    domainSourceOptions,
    providerOptions,
    engineOptions,
} from '../../types/utils'
import { UpdateReportSchema } from './yup'

// Extends the API payload with a hidden form-only boolean that the yup
// .test on service_id reads to enforce conditional-required binding.
type UpdateFormValues = UpdateReportPayload & { _requireBinding?: boolean }

const ReportWafUpdate = () => {
    const navigate = useNavigate()
    const { t } = useTranslation('report_waf')
    const { id } = useParams()

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORT_WAF, id],
        queryFn: () => getReportWaf(id!),
        enabled: !!id,
    })

    const report = data?.result

    // requireBinding is true when the customer has >= 2 bindable
    // service.report mailers — set via onMeta callback from ReportServiceSelect.
    const [requireBinding, setRequireBinding] = useState(false)

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        setError,
        formState: { errors },
    } = useForm<UpdateFormValues>({
        resolver: yupResolver(UpdateReportSchema) as any,
    })

    useEffect(() => {
        if (report) {
            reset({
                report_name: report.report_name || report.customer_name || '',
                priority: report.priority,
                send_format: report.send_format,
                domain_source: report.domain_source,
                service_customer_id: report.service_customer_id ?? '',
                provider: report.provider,
                report_engine: report.report_engine,
                domains: report.domains ?? [],
                service_id: report.service_id ?? null,
                _requireBinding: false,
            })
        }
    }, [report, reset])

    // Keep the hidden _requireBinding field in sync with the state
    // derived from ReportServiceSelect's onMeta so the yup .test sees
    // the latest value at validation time.
    useEffect(() => {
        setValue('_requireBinding', requireBinding)
    }, [requireBinding])

    const { refetch } = useQueryClientCallback({
        pattern: 'report-waf',
        mode: 'contains',
    })

    const { mutateAsync: callUpdate, isPending } = useMutationWithHandler(
        {
            mutationFn: (payload: UpdateReportPayload) =>
                updateReportWaf(id!, payload),
        },
        {
            reactQueryKeys: QUERY_KEYS.REPORT_WAF,
            successMessage: t('update_success'),
            errorMessage: t('update_error'),
            onSuccess: () => {
                refetch()
                navigate('/technical/report')
            },
        }
    )

    const onSubmit = async (values: UpdateFormValues) => {
        // Source-of-truth guard: yup .test on service_id is the primary
        // check, but this catches any race where _requireBinding sync
        // lags behind validation.
        if (requireBinding && !values.service_id) {
            setError('service_id', {
                type: 'manual',
                message: t('bound_service.required', {
                    defaultValue:
                        'A bound service is required — this customer has multiple service.report mailers. Select one to avoid ambiguous delivery.',
                }),
            })
            return
        }

        await callUpdate({
            report_name: values.report_name?.trim(),
            priority: values.priority,
            send_format: values.send_format,
            domain_source: values.domain_source,
            provider: values.provider,
            report_engine: values.report_engine,
            service_customer_id:
                values.service_customer_id?.trim() || undefined,
            domains: (values.domains ?? []).map((d: string) => d.trim()),
            service_id: values.service_id ?? null,
        })
    }

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    { path: '/technical', title: 'Technical' },
                    { path: '/technical/report', title: t('list_title') },
                    {
                        path: `/technical/report/${id}/edit`,
                        title: report?.customer_name
                            ? `${report.customer_name} — ${t('update_title')}`
                            : t('update_title'),
                    },
                ]}
            />
            {renderState() || (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Paper className="p-4 my-2">
                        <Typogaphy className="flex items-center gap-2 mb-4 text-sm font-semibold">
                            <Settings className="w-4 h-4" />
                            {t('update_title')}
                        </Typogaphy>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <FormItem
                                    control={control}
                                    name="report_name"
                                    type="textfield"
                                    label={t('fields.report_name', {
                                        defaultValue: 'Report Name',
                                    })}
                                    placeholder="e.g. VNIS"
                                />
                            </div>

                            <FormItem
                                control={control}
                                name="priority"
                                type="number"
                                label={t('priority_level')}
                                placeholder={t('priority_helper')}
                            />

                            <FormItem
                                control={control}
                                name="send_format"
                                type="select"
                                label={t('fields.send_format')}
                                options={sendFormatOptions(t)}
                            />

                            <FormItem
                                control={control}
                                name="domain_source"
                                type="select"
                                label={t('fields.domain_source')}
                                options={domainSourceOptions(t)}
                            />

                            <FormItem
                                control={control}
                                name="provider"
                                type="select"
                                label={t('fields.provider')}
                                options={providerOptions(t)}
                            />

                            <FormItem
                                control={control}
                                name="report_engine"
                                type="select"
                                label={t('fields.report_engine')}
                                options={engineOptions(t)}
                            />

                            <FormItem
                                control={control}
                                name="service_customer_id"
                                type="textfield"
                                label={t('fields.customer_id')}
                                placeholder="Service Customer ID"
                            />

                            {/* Recipients surface — read-only mailer hint
                                (edits happen at Customer > Mailers) */}
                            <div className="md:col-span-2">
                                <ServiceReportMailerHint
                                    customerId={report?.customer_id}
                                    serviceId={watch('service_id')}
                                />
                            </div>

                            {/* Service binding picker — chooses which of
                                the customer's services this report is
                                bound to so the V2 worker can resolve the
                                correct mailer group at send time. */}
                            <div className="md:col-span-2">
                                <ReportServiceSelect
                                    customerId={report?.customer_id}
                                    value={watch('service_id')}
                                    onChange={(id) =>
                                        setValue('service_id', id)
                                    }
                                    onMeta={(meta) =>
                                        setRequireBinding(meta.requireBinding)
                                    }
                                />
                                {errors.service_id && (
                                    <p className="text-xs text-destructive mt-1">
                                        {errors.service_id.message}
                                    </p>
                                )}
                            </div>

                            {/* Domains scope — card style matches the
                                Recipients card above for visual pairing */}
                            <div className="md:col-span-2">
                                <DomainsFilterCard control={control} />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/technical/report')}
                            >
                                {t('actions.cancel')}
                            </Button>
                            <Button type="submit" disabled={isPending}>
                                {t('actions.save')}
                            </Button>
                        </div>
                    </Paper>
                </form>
            )}
        </>
    )
}

export default ReportWafUpdate
